/**
 * Toki Native Bridge
 * iOS/Android WebView-тэй харилцах функцүүд
 * 
 * Toki app нь WebView дотор JSReceiver (Android) болон 
 * webkit.messageHandlers (iOS) inject хийдэг
 */

/**
 * Toki app дотор байгаа эсэхийг шалгах
 *
 * IMPORTANT: window.webkit.messageHandlers alone is NOT sufficient —
 * all iOS browsers (Chrome, Firefox, Edge) run on WKWebView and may
 * expose messageHandlers for browser features. We must check for
 * Toki-specific handlers (buyfromShoppy, setColor) that only Toki injects.
 *
 * @returns {boolean}
 */
export const isTokiWebView = () => {
    // Android WebView: Toki injects JSReceiver
    if (window.JSReceiver) {
        return true;
    }

    // iOS WebView: check for Toki-specific message handlers
    const handlers = window.webkit?.messageHandlers;
    if (handlers && (handlers.buyfromShoppy || handlers.setColor)) {
        return true;
    }

    return false;
};

/**
 * Platform тодорхойлох
 * Must match isTokiWebView() logic — check Toki-specific handlers, not generic messageHandlers.
 *
 * @returns {'android' | 'ios' | 'web'}
 */
export const getPlatform = () => {
    if (window.JSReceiver) {
        return 'android';
    }

    const handlers = window.webkit?.messageHandlers;
    if (handlers && (handlers.buyfromShoppy || handlers.setColor)) {
        return 'ios';
    }

    return 'web';
};

/**
 * Төлбөр хийх функц
 * Toki app-ын payment flow-г trigger хийнэ
 * 
 * @param {Object} options - Payment options
 * @param {string} options.merchantId - Merchant ID (Toki-с өгсөн)
 * @param {number} options.amount - Төлөх дүн
 * @param {string} options.orderId - Mini program-аас үүсгэсэн order ID
 * @param {string} options.description - Бүтээгдэхүүн/гүйлгээний тайлбар
 * @param {string} options.callbackUrl - Төлбөрийн статус илгээх callback URL
 * @returns {boolean} - Амжилттай дуудсан эсэх
 * 
 * Callback response format:
 * {
 *   orderID: string,
 *   transactionID: string,
 *   status: string,
 *   statusCode: number,
 *   transRequestId: string,
 *   token: string,
 *   amount: number
 * }
 */
export const buy = ({ merchantId, amount, orderId, description, callbackUrl }) => {
    // Validation
    if (!merchantId || !amount || !orderId || !description || !callbackUrl) {
        console.error('Toki buy: Missing required parameters');
        return false;
    }

    try {
        // Android WebView
        if (window.JSReceiver) {
            window.JSReceiver.buyfromShoppy(
                merchantId,
                `${amount}`,
                orderId,
                description,
                callbackUrl
            );
            return true;
        }

        // iOS WebView
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.buyfromShoppy) {
            const message = JSON.stringify({
                merchantId,
                amount,
                orderId,
                description,
                callbackUrl
            });
            window.webkit.messageHandlers.buyfromShoppy.postMessage(message);
            return true;
        }

        console.warn('Toki buy: Not running in Toki WebView');
        return false;
    } catch (error) {
        console.error('Toki buy error:', error);
        return false;
    }
};

/**
 * App header өнгө тохируулах
 * iOS дээр mini program-ын header өнгийг тохируулна
 * 
 * @param {string} color - Header өнгө (hex format, e.g., '#FFFFFF')
 * @param {boolean} isStatusDark - Status bar текст хар эсэх
 *   - true: 4G, цаг гэх мэт хар өнгөтэй (цайвар background-д)
 *   - false: 4G, цаг гэх мэт цагаан өнгөтэй (бараан background-д)
 * @returns {boolean} - Амжилттай дуудсан эсэх
 */
export const setColor = (color, isStatusDark = true) => {
    try {
        // Android WebView
        if (window.JSReceiver) {
            window.JSReceiver.setColor(color, isStatusDark);
            return true;
        }

        // iOS WebView
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.setColor) {
            const message = JSON.stringify({ color, isStatusDark });
            window.webkit.messageHandlers.setColor.postMessage(message);
            return true;
        }

        console.warn('Toki setColor: Not running in Toki WebView');
        return false;
    } catch (error) {
        console.error('Toki setColor error:', error);
        return false;
    }
};

/**
 * Unique order ID үүсгэх
 * 
 * @param {string} prefix - Order ID-н prefix (optional)
 * @returns {string} - Unique order ID
 */
export const generateOrderId = (prefix = 'ORD') => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${prefix}_${timestamp}_${random}`;
};

export default {
    isTokiWebView,
    getPlatform,
    buy,
    setColor,
    generateOrderId
};
